import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ButtonPrimary from '../ButtonPrimary';
import ButtonSecondary from '../ButtonSecondary';
import lockIcon from '../../img/icons/password.svg';
import errorIcon from '../../img/icons/error-triangle.svg';
import PasswordValidation from './PasswordValidation';
import style from '../../style/components/signin/Password.module.scss';
import Card from '../Card';
import InputWithError from '../InputWithError';

const Password = ({
  confirmation,
  current,
  error,
  email,
  handleCancel,
  handleConfirmIdentity,
  handleCurrent,
  handleNew1,
  handleNew2,
  handleSave,
  instruction,
  isSaveDisabled,
  newPass,
  invalid,
  nistPasswordV2,
  reauthenticationRequired,
  rules,
  validated,
  validationErrors,
  success,
}) => {
  const { t } = useTranslation();
  const resolvedInstruction =
    instruction || t('password.change.description_instruction');

  const cardProps = {
    title: t('password.change.title'),
    icon: <img className={style.img} alt="lock" src={lockIcon} />,
    className: style.wrapper,
  };

  if (reauthenticationRequired) {
    return (
      <Card {...cardProps}>
        <div className={style.form}>
          <div className={style.error} role="alert">
            <p
              id="change-password-reauthentication"
              data-testid="change-password-reauthentication"
            >
              <img src={errorIcon} alt="" />
              {t('password.change.reauthentication_message')}
            </p>
          </div>
          <div className={style.controls}>
            <ButtonPrimary
              autoFocus
              aria-describedby="change-password-reauthentication"
              onClick={handleConfirmIdentity}
            >
              {t('password.change.button_confirmIdentity')}
            </ButtonPrimary>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card {...cardProps}>
      <form
        className={`${style.form}${nistPasswordV2 ? ` ${style.formV2}` : ''}`}
        onSubmit={handleSave}
      >
        {error && Array.isArray(error) && error.length > 0 && (
          <div className={style.error}>
            {error.map((e, i) => (
              <p data-testid={`change-password-error-${i}`} key={`${e}-${i}`}>
                <img src={errorIcon} alt="" />
                {e}
              </p>
            ))}
          </div>
        )}
        <fieldset>
          <input type="hidden" name="email" value={email} />
          <label htmlFor="old-pass">
            <span>{resolvedInstruction}</span>
            <InputWithError
              id="old-pass"
              type="password"
              autoComplete="current-password"
              label={t('password.change.label_current')}
              value={current}
              onChange={handleCurrent}
              placeholder={t('password.change.placeholder_current')}
              required
            />
          </label>
          <label htmlFor="new-pass">
            <span className={style.srOnly}>
              {t('password.change.label_new')}
            </span>
            <InputWithError
              id="new-pass"
              type="password"
              autoComplete="new-password"
              label={t('password.change.label_new')}
              onChange={handleNew1}
              placeholder={t('password.change.placeholder_new')}
              value={newPass}
              className={invalid ? style.invalid : ''}
              aria-describedby={
                nistPasswordV2 ? 'new-pass-guidance' : undefined
              }
              required
            />
          </label>
          <label htmlFor="new-pass-2">
            <span className={style.srOnly}>
              {t('password.change.label_new')}
            </span>
            <InputWithError
              id="new-pass-2"
              type="password"
              autoComplete="new-password"
              label={t('password.change.label_confirm')}
              onChange={handleNew2}
              placeholder={t('password.change.placeholder_confirm')}
              value={confirmation}
              required
            />
          </label>
        </fieldset>
        {nistPasswordV2 ? (
          <PasswordValidation
            nistPasswordV2
            password={newPass}
            errors={validationErrors}
            rules={rules}
            validated={validated}
          />
        ) : (
          <PasswordValidation rules={rules} validated={validated} />
        )}
        <div className={style.controls}>
          <ButtonSecondary onClick={handleCancel}>
            {t('password.change.button_cancel')}
          </ButtonSecondary>
          <ButtonPrimary
            type="submit"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {t('password.change.button_save')}
          </ButtonPrimary>
          {success && (
            <span className={style.success}>
              {t('password.change.success_updated')}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
};

Password.propTypes = {
  confirmation: PropTypes.string.isRequired,
  current: PropTypes.string.isRequired,
  email: PropTypes.string,
  error: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleConfirmIdentity: PropTypes.func,
  handleCurrent: PropTypes.func.isRequired,
  handleNew1: PropTypes.func.isRequired,
  handleNew2: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  instruction: PropTypes.string,
  isSaveDisabled: PropTypes.bool,
  newPass: PropTypes.string.isRequired,
  invalid: PropTypes.bool,
  nistPasswordV2: PropTypes.bool,
  reauthenticationRequired: PropTypes.bool,
  rules: PropTypes.arrayOf(PropTypes.string),
  validated: PropTypes.arrayOf(PropTypes.bool),
  validationErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      params: PropTypes.object,
    }),
  ),
  success: PropTypes.bool,
};

Password.defaultProps = {
  email: '',
  handleConfirmIdentity: () => {},
  isSaveDisabled: true,
  invalid: false,
  nistPasswordV2: false,
  reauthenticationRequired: false,
  rules: [],
  validated: [],
  validationErrors: [],
  success: false,
};

export default Password;
